import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import { RootState } from './store';
import http from '../utils/http';
import { Afr } from './AfrSlice';
import { ReviewsRequestFilter } from './AfrReviewSlice';
import { TaxonomyVersionInfo } from './TaxonomyVersionSlice'; 
import { EntityReference } from './EntitySlice';



export interface DocumentReference {
    uri: string;
    content: string;
    mimeType: string;
    documentId: string | null;
}

export interface DocumentRelationship {
    timestamp: string;
    role: string;
    documentId: string | null;
    name: string;
    affiliate: string | null;
}

export interface GenericDocumentResponse {
    uploadDocument: string;
    error: DocumentError;
}

export interface DocumentError {
    message: string;
    stackTrace: string;
    content: string;
}

export interface DocumentNotification {
    subject: string;
    description: string;
    currentStep: number;
    totalSteps: number;
}

export interface DocumentStatusInterface {
    progress: number;
    status: "info" | "success" | "danger"
    message: string;
    isEnabled: boolean;
}

export interface FileSender {
    file: File;
    fileType: string;
}

export interface AfrImportSender {
    entityCode: string;
    afrYear: number;
    file: File;
    fileType: string;
    sections: string[];
}

export interface BulkImportSender {
    reportRef: string;
    afrYear: number;
    directory: string;
    fileType: string;
}

export interface TaxonomyImportSender {
    taxonomyVersion: TaxonomyVersionInfo;
    file: File | null;
    fileType: string;
}

export interface DocumentContent {
    documentData: string;
    documentType: string;
}

export interface ImportAfr {
    report: DocumentContent;
    afr: Afr;
}

export interface TemplateQuery {
    afrId: string;
    populate: boolean;
}

export interface AFRDocumentStarter {
    entityCode: string;
    afrYear: number;
    fileType: string;
    outputType: string | null;
    populate: 'NO' | 'CURRENT_YEAR' | 'PRIOR_YEAR';
}

export interface AFRImportStarter {
    entityCode: string;
    afrYear: number;
    fileType: string;
    sections: string[];
    contents: DocumentContent;
}

export interface TaxonomyImportStarter {
    taxonomy: TaxonomyVersionInfo;
    fileType: string;
    contents: DocumentContent;
}

export interface TaskLogStarter {
    taskLogFilter: ReviewsRequestFilter;
}

export interface DocumentsState {
    documents: DocumentReference;
    status: 'idle' | 'loading' | 'failed';
    documentStatus: DocumentStatusInterface;
    error: DocumentError | null;
}

export interface DocumentInput {
    description: string;
    extension: string;
    name: string;
    mimeType: string;
    content: string;
}

const initialState: DocumentsState = {
    documents: { uri: "", content: "", mimeType: "", documentId: null },
    status: "idle",
    documentStatus: { isEnabled: false, message: "", progress: 0, status: "info" },
    error: null
}

export const uploadFile = async function (file: File, description: string, dispatch : any) {

    var documentContent = await fileToBase64(file);
    var fileNameEnding = file.name.split(".").pop();
    let extensionValue = "";
    if (fileNameEnding != undefined) {
        extensionValue = "." + fileNameEnding;
    }
    
    //TODO: here you can add relevent details like entity id and/or year
    let auditDocument: DocumentInput = {
        description: description,
        name: file.name,
        extension: extensionValue,
        mimeType: file.type,
        content: documentContent,
    };

    // save document to document slice
    const response = await dispatch(uploadDocument(auditDocument));
    const importDoc: GenericDocumentResponse = unwrapResult(response);

    return importDoc.uploadDocument;
}

export const uploadTemplateFile = async function (file: File, description: string, dispatch: any) {

    var documentContent = await fileToBase64(file);
    var fileNameEnding = file.name.split(".").pop();
    let extensionValue = "";
    if (fileNameEnding != undefined) {
        extensionValue = "." + fileNameEnding;
    }

    //TODO: here you can add relevent details like entity id and/or year
    let templateDocument: DocumentInput = {
        description: description,
        name: file.name,
        extension: extensionValue,
        mimeType: file.type,
        content: documentContent,
    };

    // save document to document slice
    const response = await dispatch(uploadTemplateDocument(templateDocument));
    const importDoc: string = unwrapResult(response);

    return importDoc;
}

export const getDataCollectionTemplate = createAsyncThunk(
    "Documents/GetDataCollectionTemplate",
    async (tq: TemplateQuery): Promise<DocumentReference | null> => {
        try {
            const result = await http<undefined, DocumentReference>({
                path: `/document/${tq.afrId}/${tq.populate}`,
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getTemplate = createAsyncThunk(
    "Documents/GetTemplate",
    async (afrData: AFRDocumentStarter)
        : Promise<DocumentReference | null> => {
        try {
            const result = await http<AFRDocumentStarter, DocumentReference>({
                path: `/document/template`,
                method: 'post',
                body: afrData
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getTemplateByDescription = createAsyncThunk(
    "Documents/GetTemplateByDescription",
    async (templateName: string)
        : Promise<DocumentReference | null> => {
        try {
            const result = await http<string, DocumentReference>({
                path: `/document/downloadTemplateByDescription`,
                method: 'post',
                body: templateName
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getAfrInstance = createAsyncThunk(
    "Documents/GetAfrInstance",
    async (afrData: AFRDocumentStarter)
        : Promise<DocumentReference | null> => {
        try {
            const result = await http<AFRDocumentStarter, DocumentReference>({
                path: `/document/afrInstance`,
                method: 'post',
                body: afrData
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            
            return null;
        }
    });

export const getTaxonomy = createAsyncThunk(
    "Documents/GetTaxonomy",
    async (taxRequest: AFRDocumentStarter)
        : Promise<DocumentReference | null> => {
        try {
            const result = await http<AFRDocumentStarter, DocumentReference>({
                path: `/document/getTaxonomy`,
                method: 'post',
                body: taxRequest
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });


export const sendFile = createAsyncThunk(
    "Documents/SendFile",
    async (file: FileSender): Promise<DocumentReference | null> => {
        try {

            var base64String;
            base64String = await fileToBase64(file.file);

            let documentData: DocumentContent
                = { documentData: base64String, documentType: file.fileType };

            const result = await http<DocumentContent, DocumentReference>({
                path: `/document/uploadTestDocument`,
                method: 'post',
                body: documentData
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const uploadAuditDocument = createAsyncThunk(
    "Documents/UploadAuditDocument",
    async (afrAudit: { entityCode: string; afrYear: number; file: File }): Promise<string | null> => {
        try {

            var base64String;
            base64String = await fileToBase64(afrAudit.file);

            let documentData: DocumentContent
                = { documentData: base64String, documentType: afrAudit.file.type };

            let afrAuditData: { entityCode: string; afrYear: number; documentContent: DocumentContent }
                = { entityCode: afrAudit.entityCode, afrYear: afrAudit.afrYear, documentContent: documentData };

            const result = await http<{ entityCode: string; afrYear: number; documentContent: DocumentContent }, string>({
                path: `/document/uploadAfrAudit`,
                method: 'post',
                body: afrAuditData
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const uploadDocument = createAsyncThunk(
    "Documents/UploadDocument",
    async (document: DocumentInput): Promise<GenericDocumentResponse | null> => {
        try {
            const result = await http<DocumentInput, GenericDocumentResponse>({
                path: `/document/uploadDocument`,
                method: `post`,
                body: document
            });
            var resultReturned : GenericDocumentResponse | null = null;

            if (result.ok && result.parsedBody) {
                resultReturned = result.parsedBody;

                if (resultReturned.error) {
                    /* return the exception to redux */
                    throw new Error(JSON.stringify(resultReturned.error));
                }
                return resultReturned;

            }
            return null;

        } catch (ex) {
            throw ex;
        }
    });

export const uploadTemplateDocument = createAsyncThunk(
    "Documents/UploadTemplateDocument",
    async (document: DocumentInput): Promise<string | null> => {
        try {
            const result = await http<DocumentInput, string>({
                path: `/document/uploadTemplateDocument`,
                method: `post`,
                body: document
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            throw ex;
        }
    });

export const downloadDocumentById = createAsyncThunk(
    "Documents/DownloadDocument",
    async(documentId: string): Promise<DocumentReference | null> => {
        try {
        const result = await http<string, DocumentReference>({
            path: `/document/downloadDocument`,
            method: `post`,
            body: documentId
        });

            if (result.ok && result.parsedBody) {
                if (result.parsedBody.content &&
                    result.parsedBody.content != "") {
                    downloadDocumentToBrowser(result.parsedBody);
                }           
            return result.parsedBody;
        }
        else {
            return null;
        }
    } catch (ex) {
        return null;
    }
});

export const downloadDocumentInfoById = createAsyncThunk(
    "Documents/DownloadDocumentInfo",
    async (documentId: string): Promise<DocumentReference | null> => {
        try {
            const result = await http<string, DocumentReference>({
                path: `/document/documentInfo`,
                method: `post`,
                body: documentId
            });

            if (result.ok && result.parsedBody) {
                if (result.parsedBody.content &&
                    result.parsedBody.content != "") {
                    downloadDocumentToBrowser(result.parsedBody);
                }
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const importAfrSpreadsheetFile = createAsyncThunk(
    "Documents/ImportAfrSpreadsheetFile",
    async (file: AfrImportSender): Promise<ImportAfr | null> => {
        try {
            var base64String;
            base64String = await fileToBase64(file.file);
            let documentData: DocumentContent
                = { documentData: base64String, documentType: file.fileType };
            let importStarter: AFRImportStarter =
            {
                entityCode: file.entityCode,
                afrYear: file.afrYear,
                fileType: '',
                sections: file.sections,
                contents: documentData
            };
            const result = await http<AFRImportStarter, ImportAfr>({
                path: `/document/importAfrSpreadsheet`,
                method: 'post',
                body: importStarter
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const importFinancialReportSpreadsheetFile = createAsyncThunk(
    "Documents/ImportFinancialReportSpreadsheetFile",
    async (file: AfrImportSender): Promise<ImportAfr | null> => {
        try {
            var base64String;
            base64String = await fileToBase64(file.file);
            let documentData: DocumentContent
                = { documentData: base64String, documentType: file.fileType };
            let importStarter: AFRImportStarter =
            {
                entityCode: file.entityCode,
                afrYear: file.afrYear,
                fileType: '',
                sections: file.sections,
                contents: documentData
            };
            const result = await http<AFRImportStarter, ImportAfr>({
                path: `/document/importFinancialReportSpreadsheet`,
                method: 'post',
                body: importStarter
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const bulkImportFinancialReportSpreadsheetFile = createAsyncThunk(
    "Documents/BulkImportFinancialReportSpreadsheetFile",
    async (sender: BulkImportSender): Promise<DocumentReference | null> => {
        try {
            let documentData: DocumentContent
                = { documentData: sender.directory, documentType: sender.fileType };
            let importStarter: AFRImportStarter =
            {
                entityCode: sender.reportRef,
                afrYear: sender.afrYear,
                fileType: '',
                sections: [],
                contents: documentData
            };
            const result = await http<AFRImportStarter, DocumentReference>({
                path: `/document/bulkImportFinancialReportSpreadsheet`,
                method: 'post',
                body: importStarter
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const importAfrInstanceFile = createAsyncThunk(
    "Documents/ImportAfrInstanceFile",
    async (file: AfrImportSender): Promise<ImportAfr | null> => {
        try {
            var base64String;
            base64String = await fileToBase64(file.file);
            let documentData: DocumentContent
                = { documentData: base64String, documentType: file.fileType };
            let importStarter: AFRImportStarter =
            {
                entityCode: file.entityCode,
                afrYear: file.afrYear,
                fileType: '',
                sections: file.sections,
                contents: documentData
            };
            const result = await http<AFRImportStarter, ImportAfr>({
                path: `/document/importAfrInstance`,
                method: 'post',
                body: importStarter
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const importTaxonomySpreadsheetFile = createAsyncThunk(
    "Documents/ImportTaxonomySpreadsheetFile",
    async (file: TaxonomyImportSender): Promise<DocumentContent | null> => {
        try {
            var base64String;
            if (file.file) {
                base64String = await fileToBase64(file.file);
            } else {
                base64String = "";
            }
            let documentData: DocumentContent
                = { documentData: base64String, documentType: file.fileType };
            let importStarter: TaxonomyImportStarter =
            {
                taxonomy: file.taxonomyVersion,
                fileType: '',
                contents: documentData
            };
            const result = await http<TaxonomyImportStarter, DocumentContent>({
                path: `/document/importTaxonomySpreadsheet`,
                method: 'post',
                body: importStarter
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });


export const getSampleDocument = createAsyncThunk(
    "Documents/GetSampleDocument",
    async (afrData: AFRDocumentStarter): Promise<DocumentReference | null> => {
        try {
            const result = await http<AFRDocumentStarter, DocumentReference>({
                path: `/document/sample`,
                method: "post",
                body: afrData
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getWebLog = createAsyncThunk(
    "Documents/GetWebLog",
    async (): Promise<DocumentReference | null> => {
        try {
            const result = await http<null, DocumentReference>({
                path: `/document/getWebLog`,
                method: "post",
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getAPILog = createAsyncThunk(
    "Documents/GetAPILog",
    async (): Promise<DocumentReference | null> => {
        try {
            const result = await http<null, DocumentReference>({
                path: `/document/getAPILog`,
                method: "post",
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getAFRReport = createAsyncThunk(
    "Documents/GetAFRReport",
    async (afrData: AFRDocumentStarter): Promise<DocumentReference | null> => {
        try {
            const result = await http<AFRDocumentStarter, DocumentReference>({
                path: `/document/afrReport`,
                method: "post",
                body: afrData
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const generateTaskLog = createAsyncThunk(
    "Documents/GenerateTaskLog",
    async (taskLogData: TaskLogStarter): Promise<DocumentReference | null> => {
        try {
            const result = await http<TaskLogStarter, DocumentReference>({
                path: `/document/generateTaskLog`,
                method: "post",
                body: taskLogData
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    });

export const getExistingTemplates = createAsyncThunk(
    "Documents/GetExistingTemplates",
    async (): Promise<[] | null> => {
        try {
            const result = await http<null, []>({
                path: `/document/getExistingTemplates`,
                method: "get"
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    })

export const getBackedUpTemplates = createAsyncThunk(
    "Documents/GetBackedUpTemplates",
    async (): Promise<[] | null> => {
        try {
            const result = await http<null, []>({
                path: `/document/getBackedUpTemplates`,
                method: "get"
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    }

)

export const migrateExistingTemplates = createAsyncThunk(
    "Documents/MigrateExistingTemplates",
    async (templateNameValues: Array<string>): Promise<string | null> => {
        try {
            const result = await http<Array<string>, string>({
                path: `/document/migrateExistingTemplates`,
                method: "post",
                body: templateNameValues
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }
    })

export const deleteExistingTemplate = createAsyncThunk(
    "Documents/DeleteExistingTemplate",
    async (templateNameValue: string): Promise<string | null> => {

        try {
            const result = await http<string, string>({
                path: `/document/deleteExistingTemplate`,
                method: "post",
                body: templateNameValue
            });
            if (result.ok && result.parsedBody) {
                return result.parsedBody;
            }
            else {
                return null;
            }
        } catch (ex) {
            return null;
        }

    })

export const fileToBase64 = (file: File): Promise<string> => {

    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = () => resolve(reader.result ? reader.result.toString() : "");
        reader.onerror = error => reject(error);
    })
}

export const downloadDocumentToBrowser = async (doc: DocumentReference) => {
    if (doc != null) {
        var url = "data:" + doc.mimeType + ";base64," + doc.content;
        await fetch(url)
            .then(res => res.blob())
            .then(newBlob => {
                const data = window.URL.createObjectURL(newBlob);
                var link = document.createElement("a");
                link.href = data;
                link.download = doc.uri;
                link.click();
                setTimeout(function () {
                    window.URL.revokeObjectURL(data);
                })
            });
    }
}


export const documentsSlice = createSlice({
    name: 'Documents',
    initialState,
    reducers: {
        updateDocumentNotification: (state: any, action: any) => {

            let newDocumentStatus: DocumentStatusInterface = state.documentStatus;
            newDocumentStatus.isEnabled = true;
            newDocumentStatus.message = action.payload.description;
            newDocumentStatus.progress = (action.payload.currentStep / action.payload.totalSteps) * 100;

            if (action.payload.currentStep < action.payload.totalSteps) {
                newDocumentStatus.status = "info";
            }
            else if (action.payload.currentStep == action.payload.totalSteps) {
                newDocumentStatus.status = "success";
            }
            else if (action.payload.currentStep > action.payload.totalSteps) {
                newDocumentStatus.status = "danger";
            }
        },
        resetError: (state: any, action: any) => {
            if (state) {
                state.error = null;
            }
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(getDataCollectionTemplate.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getDataCollectionTemplate.fulfilled, (state: any, action: any) => {
                if (action.payload != null) {
                    state.documents = action.payload;
                }
                state.status = "idle";
            })
            .addCase(getSampleDocument.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getSampleDocument.fulfilled, (state: any, action: any) => {
                if (action.payload != null) {
                    state.documents = action.payload;
                }
                state.status = "idle";
            })          
            .addCase(getAFRReport.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getAFRReport.fulfilled, (state: any, action: any) => {
                if (action.payload != null) {
                    state.documents = action.payload;
                }
                state.status = "idle";
            })
            .addCase(uploadDocument.pending, (state: any) => {
                state.status = "loading"
            })
            .addCase(uploadDocument.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    const { uploadDocument, error } = action.payload;
                    state.documents.documentId = uploadDocument;
                    state.error = error;
                }
                state.status = "idle";
            })
            .addCase(uploadDocument.rejected, (state: any, action: any) => {
                var errorAPI: DocumentError = action.error.message ? JSON.parse(action.error.message) : "unknown error uploading document";
                state.error = errorAPI;
                state.status = "idle"
            })
            .addCase(downloadDocumentById.pending, (state: any) => {
                state.status = "loading"
            })
            .addCase(downloadDocumentById.fulfilled, (state: any, action: any) => {
                if (action.payload) {
                    state.documents = action.payload;
                }
                state.status = "idle";
            })
            .addCase(generateTaskLog.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(generateTaskLog.fulfilled, (state: any, action: any) => {
                if (action.payload != null) {
                    state.documents = action.payload;
                }
                state.status = "idle";
            })
            .addCase(getWebLog.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getWebLog.fulfilled, (state: any, action: any) => {
                if (action.payload != null) {
                    /*state.documents = action.payload;*/
                }
                state.status = "idle";
            })
            .addCase(getAPILog.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getAPILog.fulfilled, (state: any, action: any) => {
                if (action.payload != null) {
                    /*state.documents = action.payload;*/
                }
                state.status = "idle";
            })
            .addCase(uploadAuditDocument.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(uploadAuditDocument.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(getExistingTemplates.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getExistingTemplates.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(getBackedUpTemplates.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getBackedUpTemplates.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(migrateExistingTemplates.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(migrateExistingTemplates.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(getTemplateByDescription.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(getTemplateByDescription.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(uploadTemplateDocument.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(uploadTemplateDocument.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
            .addCase(deleteExistingTemplate.pending, (state: any) => {
                state.status = "loading";
            })
            .addCase(deleteExistingTemplate.fulfilled, (state: any, action: any) => {
                state.status = "idle";
            })
    },
});

export const { updateDocumentNotification, resetError } = documentsSlice.actions;

export const selectDocuments = (state: RootState) => state.documents.documents;
export const selectDocumentStatus = (state: RootState) => state.documents.documentStatus;
export const getErrors = (state: RootState) => state.documents.error ? state.documents.error : null;

export default documentsSlice.reducer;